import { createRoute } from "@tanstack/react-router";
import { CheckCircle2, Smartphone } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { SubPage } from "../components/layout.tsx";
import { Button, ErrorBlock, Price, Spinner } from "../components/ui.tsx";
import { usePay } from "../lib/use-pay.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopPay } from "./desktop/pay.tsx";

/**
 * 收银台（手机版）。
 *
 * 🔴 **金额不在这一页算。** 只把订单 id 交给服务端，钱由它从库里的 payableAmount
 *    加出来 —— 前端能改的数字一律不能进收款。
 *
 * ⚠️ 逻辑全在 `usePay()` 里，这里只管版面。
 */

/** 一笔待付的钱：给个跳转按钮，顺带把链接明文放出来（桌面端好复制到手机上付）。 */
function PayButton({
  payUrl,
  label,
  amount,
}: {
  payUrl: string;
  label: string;
  amount?: number;
}) {
  return (
    <div className="border-t border-neutral-100 px-4 py-3 first:border-0">
      <div className="flex items-center gap-3">
        <span className="min-w-0 flex-1 text-sm">
          {label}
          {amount !== undefined && (
            <span className="ml-2 text-brand-600">
              <Price cents={amount} />
            </span>
          )}
        </span>
        <Button size="sm" onClick={() => window.location.assign(payUrl)}>
          去付款
        </Button>
      </div>
      <p className="mt-1 truncate text-xs text-ink-500">{payUrl}</p>
    </div>
  );
}

function Pay() {
  const p = usePay();
  const { instruction, result, poll } = p;

  if (p.orderIds.length === 0) {
    return (
      <SubPage title="收银台">
        <ErrorBlock error={new Error("没有要支付的订单")} />
      </SubPage>
    );
  }

  if (p.done) {
    return (
      <SubPage title="收银台">
        <div className="flex flex-col items-center gap-3 py-16">
          <CheckCircle2 className="size-12 text-emerald-500" />
          <p className="text-sm">支付成功，正在返回订单…</p>
        </div>
      </SubPage>
    );
  }

  /**
   * ── 线下收款 ───────────────────────────────────────────────
   *
   * 🔴 **这个商城不走线上支付**：线上只建订单，收款走别的渠道。
   *    这一页要回答买家三个问题：**给谁、给多少、然后呢**。
   */
  if (p.offline && instruction.data) {
    const inst = instruction.data;

    /** 商家确认收款之后这一页自己会变 —— 不用他刷新，也不用他再点一次。 */
    if (inst.payment !== "pending") {
      return (
        <SubPage title="待付款">
          <div className="flex flex-col items-center gap-3 py-16">
            <CheckCircle2 className="size-12 text-emerald-500" />
            <p className="text-sm">商家已确认收款，正在备货</p>
            <Button size="sm" onClick={p.goOrders}>
              查看订单
            </Button>
          </div>
        </SubPage>
      );
    }

    return (
      <SubPage title="待付款">
        <div className="space-y-3 p-3">
          <section className="rounded-xl border border-neutral-200 bg-white p-4 text-center">
            <p className="text-[13px] text-ink-500">需付款</p>
            <p className="mt-1 text-3xl">
              <Price cents={inst.payableAmount} className="text-brand-600" />
            </p>
          </section>

          <section className="rounded-xl border border-neutral-200 bg-white">
            <div className="border-b border-neutral-100 px-4 py-2.5 text-[13px] text-ink-500">
              把货款付给
            </div>
            <div className="space-y-1 px-4 py-3">
              <p className="text-[15px] font-medium">{inst.payee.name}</p>
              {/*
                ⚠️ **只给联系方式，不给收款账号。**
                   账号变更频繁（换卡、换收款码），存进库里过期了
                   就是把钱打给一个作废的账户；而且落库意味着任何能读
                   商家资料的人都能拿到他的账户。让双方直接联系最稳。
              */}
              {inst.payee.contactPhone ? (
                <a
                  href={`tel:${inst.payee.contactPhone}`}
                  className="inline-flex items-center gap-1.5 text-[15px] text-brand-600"
                >
                  <Smartphone className="size-4" />
                  {inst.payee.contactPhone}
                  {inst.payee.contactName && (
                    <span className="text-ink-500">（{inst.payee.contactName}）</span>
                  )}
                </a>
              ) : (
                <p className="text-[13px] text-ink-500">
                  联系方式还没填，请到店铺页或联系客服获取
                </p>
              )}
            </div>
          </section>

          <section className="rounded-xl border border-neutral-200 bg-white px-4 py-3 text-[13px] leading-relaxed text-ink-500">
            <p className="mb-1 font-medium text-ink-900">怎么走</p>
            <p>
              ① 联系上面这位，按他给的方式付款（转账 / 微信 / 现金 / 对公都行）
              <br />
              ② 他在后台确认收款
              <br />
              ③ 确认后开始备货发货，这一页会自动变
            </p>
            {inst.expireAt && (
              /*
                ⚠️ 超时会释放库存，必须说在前面 ——
                   不说的话买家钱都转出去了才发现单子没了。
              */
              <p className="mt-2 text-amber-700">
                请在 {new Date(inst.expireAt).toLocaleString("zh-CN", { hour12: false })} 前完成，
                逾期订单会自动取消并释放库存。
              </p>
            )}
          </section>

          <Button variant="outline" className="w-full" onClick={p.goOrders}>
            我知道了，去订单列表
          </Button>
        </div>
      </SubPage>
    );
  }

  if (instruction.isLoading) return <Spinner label="正在确认付款方式" />;

  return (
    <SubPage title="收银台">
      {p.create.isPending && <Spinner label="正在创建收款单" />}
      {p.create.error && <ErrorBlock error={p.create.error} onRetry={() => p.create.mutate()} />}

      {result && !result.success && <ErrorBlock error={new Error(result.error)} />}

      {result?.success && !result.split && (
        <>
          <div className="bg-brand-50 px-4 py-2.5 text-xs text-brand-700">
            <p className="flex items-center gap-1.5">
              <Smartphone className="size-3.5" />
              用手机打开下面的链接付款，付完这一页会自动跳转
            </p>
          </div>
          <div className="mt-2 bg-white">
            <PayButton payUrl={result.payUrl} label="支付宝 / 微信" />
            {result.wechatPayUrl && <PayButton payUrl={result.wechatPayUrl} label="微信" />}
          </div>
        </>
      )}

      {result?.success && result.split && (
        <>
          {/*
            🔴 必须说清楚还要付几笔，否则用户付完第一笔就走了，货永远发不出去。
            ⚠️ 续付时列出来的只是**没付的那几笔**，标题要跟着变 ——
               写死"分 5 笔"而下面只有 3 笔，用户会以为系统错了。
          */}
          <div className="bg-amber-50 px-4 py-3 text-xs text-amber-800">
            <p className="font-medium">
              {result.subOrders.length < result.splitTotal
                ? `这一单分 ${result.splitTotal} 笔付，还差 ${result.subOrders.length} 笔`
                : `这笔金额超过了单次收款上限，需要分 ${result.splitTotal} 笔付款`}
            </p>
            <p className="mt-0.5">
              <strong>付满整组才会发货</strong>。中途关掉也没关系，回到订单页可以接着付，
              已经付进去的不会作废。
            </p>
          </div>
          <div className="mt-2 bg-white">
            {result.subOrders.map((sub, i) => (
              <PayButton
                key={sub.orderNo}
                payUrl={sub.payUrl}
                label={`第 ${i + 1} 笔`}
                amount={sub.uniqueAmountCents}
              />
            ))}
          </div>
        </>
      )}

      {poll.data && (
        <p className="px-4 py-6 text-center text-xs text-ink-500">
          {poll.data.groupTotal !== undefined
            ? `已付 ${p.paidCount}/${poll.data.groupTotal} 笔，付满才会发货…`
            : poll.data.status === "processing"
              ? "钱已收到，正在处理…"
              : "等待付款…"}
        </p>
      )}
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与逻辑两边共用）。 */
function PayRoute() {
  return useIsDesktop() ? <DesktopPay /> : <Pay />;
}

export const payRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pay",
  component: PayRoute,
  validateSearch: (s: Record<string, unknown>): { orderIds?: string } => ({
    orderIds: s.orderIds ? String(s.orderIds) : undefined,
  }),
});
